import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Phone, LogOut } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { useLogout } from '../hooks/useLogout';
import { translateError } from '../lib/translate-error';

/** For someone signed in with no line of their own and no one else's to help with.
 *
 *  Reached two ways: a delegate whose access was revoked, and anyone whose invite was
 *  to an account they have since left. Before `claimOwnAccount` existed this was a dead
 *  end — `signUp` is the only other path that creates an account and it creates the
 *  User too, so it could only ever answer "email already in use".
 *
 *  Sign-out lives here because this screen sits outside the shell that normally holds
 *  it, and a dead end you cannot leave is worse than the dead end. */
export default function ClaimLinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const crud = useClientQueries(schema);
  const claim = crud.$procs.claimOwnAccount.useMutation();

  async function onClaim() {
    try {
      const account = (await claim.mutateAsync({})) as { id: string };
      // `myAccounts` is what the router routes on, so it has to be refetched before we
      // navigate — otherwise AccountScope reads the old empty list and bounces straight
      // back here. Invalidating everything rather than one key: a procedure mutation
      // cannot tell the cache which queries it touched, and this runs once in an
      // account's life.
      await queryClient.invalidateQueries();
      navigate(`/a/${account.id}`, { replace: true });
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12 sm:py-20 text-center space-y-5">
      <Phone className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden />
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">{t('claimLine.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('claimLine.body')}</p>
      </div>
      <button
        type="button"
        onClick={onClaim}
        disabled={claim.isPending}
        className="btn-primary w-full"
      >
        {claim.isPending ? t('claimLine.creating') : t('claimLine.submit')}
      </button>
      <button
        type="button"
        onClick={() => void logout()}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <LogOut className="h-4 w-4" aria-hidden />
        {t('action.signOut')}
      </button>
    </div>
  );
}
